import React from "react";
import { APP_VERSION } from "../version";
import { useAppUpdate } from "../useAppUpdate";

// ── Versiomerkki yläpalkissa ──────────────────────────────────
//
// Versionumero oli ennen vain sivun alalaidassa, "Spinloop v4.7.1"
// -kortissa. Se on oikea paikka tiedolle, mutta väärä paikka
// kysymykselle *"onko tämä se uusi versio"* — siihen asti pitää vierittää
// koko sivu alas.
//
// 2.11.2026 tämä maksoi puoli tuntia: julkaisu ajettiin, taulukko ei
// muuttunut, eikä ruudulla ollut mitään mikä olisi kertonut kumpi versio
// selaimessa oli. Nyt se lukee yläkulmassa.
//
// Merkki näyttää kaksi eri asiaa, ja ero niiden välillä on koko pointti:
//
//   v4.7.1            selain ja palvelin ovat samassa versiossa
//   v4.7.0 → 4.7.1    palvelimella on uudempi, selaimessa vanha
//
// Jälkimmäinen on napautettava ja lataa sivun uudelleen. Alalaidan
// päivitysbanneri (App.tsx) sanoo saman isommin; tämä on se, joka näkyy
// ilman vierittämistä ja kertoo myös numerot.
//
// Jos julkaisu ei mennyt perille lainkaan, merkissä lukee pelkkä vanha
// numero ilman nuolta — palvelimen versio on silloin sama vanha. Sekin on
// vastaus, ja juuri se jota 2.11. jäätiin arvailemaan.
//
// Natiiviapissa nuolta ei tule koskaan: `useAppUpdate` ei kysy palvelimelta
// mitään, koska appi paketoi oman kopionsa web-sisällöstä ja päivitys
// tulee kaupasta.

interface Props {
  lang?: "fi" | "en";
  /** Tekstin väri. Oletus seuraa teeman vaimeaa sävyä. */
  color?: string;
  style?: React.CSSProperties;
}

const VersionTag: React.FC<Props> = ({ lang = "fi", color, style }) => {
  const { updateReady, serverVersion, reload } = useAppUpdate();
  const fi = lang !== "en";
  const base: React.CSSProperties = {
    fontSize: 10, fontFamily: "'Sora', sans-serif", lineHeight: 1.2,
    letterSpacing: "0.02em", ...style,
  };

  if (updateReady && serverVersion) {
    return (
      <button
        onClick={reload}
        title={fi
          ? `Selaimessa on versio ${APP_VERSION}, palvelimella ${serverVersion}. Napauta ladataksesi uuden.`
          : `This browser runs ${APP_VERSION}, the server has ${serverVersion}. Tap to load the new one.`}
        style={{
          ...base, color: "#b45309", background: "rgba(180,83,9,0.10)",
          border: "1px solid rgba(180,83,9,0.30)", borderRadius: 999,
          padding: "1px 7px", cursor: "pointer", fontWeight: 700,
        }}
      >
        v{APP_VERSION} → {serverVersion}
      </button>
    );
  }

  return (
    <span
      title={fi ? "Tässä selaimessa ajossa oleva versio" : "The version running in this browser"}
      style={{ ...base, color: color ?? "var(--sl-muted)" }}
    >
      v{APP_VERSION}
    </span>
  );
};

export default VersionTag;
